"use strict";

/**
 * src/pages/analysis/telegramBookmakerDraft.shared.js
 *
 * PHASE 2D.2 — Pure draft/validation/equality helpers for the Telegram
 * Arb-tab bookmaker selector. These helpers let
 * TelegramNotificationSettings.jsx build a local UI draft from the
 * server's `bookmakers: null | string[]` wire value, validate that
 * draft before saving, and compare it against server settings for
 * dirty-checking — WITHOUT re-declaring or copying the 48-item
 * sportsbook catalog anywhere in this file.
 *
 * IMPORTANT — PROVISIONAL IDs, NOT CANONICAL SHARP KEYS:
 *   Every id accepted/produced by this module is a PROVISIONAL
 *   INTERNAL UI ID from sportsbookCatalog.shared.js. It is NOT a
 *   canonical Sharp key. This module does not invent, alias, or infer
 *   any raw Sharp key from a provisional id.
 *
 * Module contract:
 *   - Plain CommonJS (module.exports = {...}).
 *   - No browser globals (window / document / navigator / localStorage
 *     / etc.), no environment variables, no filesystem access, no
 *     network access, no database access.
 *   - No `console.*` calls anywhere in this file.
 *   - No side effects at require-time beyond requiring
 *     ./sportsbookCatalog.shared and declaring the functions below.
 *   - Never mutates its inputs (draft objects, id arrays) or the
 *     shared catalog it reads from.
 *   - Does not declare any sportsbook list of its own; every id
 *     decision is delegated to sportsbookCatalog.shared.js.
 */

const {
  SELECTABLE_SPORTSBOOK_IDS,
  normalizeSportsbookId,
  isSelectableSportsbookId,
} = require("./sportsbookCatalog.shared");

// =========================================================================
// Modes
// =========================================================================

const TELEGRAM_BOOKMAKER_FILTER_MODES = Object.freeze({
  UNLIMITED: "unlimited",
  CUSTOM: "custom",
});

// =========================================================================
// Internal helpers (no exports)
// =========================================================================

/**
 * Strict "plain object" check: rejects null, arrays, Date, Map, Set,
 * class instances, and anything whose prototype chain isn't exactly
 * Object.prototype (or null-prototype). Never throws — any access
 * error (e.g. a hostile Proxy trap) is swallowed and treated as "not
 * a plain object".
 *
 * @param {*} value
 * @returns {boolean}
 */
function isPlainObject(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch (_e) {
    return false;
  }
}

// =========================================================================
// normalizeTelegramBookmakerIds
// =========================================================================

/**
 * Normalizes an arbitrary value into a de-duplicated array of
 * currently-selectable provisional sportsbook ids, ordered exactly as
 * SELECTABLE_SPORTSBOOK_IDS (catalog order), regardless of the
 * click/input order the ids arrived in.
 *
 * - Non-array input (including null/undefined/objects/strings) → [].
 * - Each entry is normalized via the catalog's own
 *   normalizeSportsbookId, then kept only if
 *   isSelectableSportsbookId(...) === true (drops unknown ids and the
 *   two inactive ids: 188bet, betsson).
 * - Duplicates collapse to a single occurrence.
 * - Never mutates `value`. Always returns a brand-new array.
 *
 * @param {*} value
 * @returns {string[]}
 */
function normalizeTelegramBookmakerIds(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const selectedSet = new Set();

  try {
    value.forEach((entry) => {
      const normalized = normalizeSportsbookId(entry);
      if (normalized && isSelectableSportsbookId(normalized)) {
        selectedSet.add(normalized);
      }
    });
  } catch (_e) {
    // Fail-closed on any unexpected iteration error (e.g. a hostile
    // Proxy trap): treat as if nothing could be normalized.
    return [];
  }

  return SELECTABLE_SPORTSBOOK_IDS.filter((id) => selectedSet.has(id));
}

// =========================================================================
// buildTelegramBookmakerDraftFromSettings
// =========================================================================

/**
 * Builds a local UI draft `{ mode, selectedIds }` from the server's
 * `bookmakers` wire value for the Arb tab.
 *
 * - `null` → unlimited draft with an empty selection list.
 * - An array (including an EMPTY array) → custom draft, normalized
 *   through normalizeTelegramBookmakerIds. An empty array is
 *   deliberately NOT treated as "unlimited" — collapsing a
 *   previously-saved empty custom selection into "unlimited" would
 *   silently widen an already-narrowed filter.
 * - Any other shape (not null, not an array) fails closed to an empty
 *   custom draft. This never throws and never resolves to
 *   "unlimited" for an unrecognized shape.
 *
 * Always returns a brand-new object (and, for custom, a brand-new
 * array); never returns/aliases the input.
 *
 * @param {null|Array<*>|*} bookmakers
 * @returns {{mode: "unlimited"|"custom", selectedIds: string[]}}
 */
function buildTelegramBookmakerDraftFromSettings(bookmakers) {
  if (bookmakers === null) {
    return { mode: TELEGRAM_BOOKMAKER_FILTER_MODES.UNLIMITED, selectedIds: [] };
  }

  if (Array.isArray(bookmakers)) {
    return {
      mode: TELEGRAM_BOOKMAKER_FILTER_MODES.CUSTOM,
      selectedIds: normalizeTelegramBookmakerIds(bookmakers),
    };
  }

  // Unknown shape: fail closed to an empty custom draft (never throw,
  // never silently become "unlimited").
  return { mode: TELEGRAM_BOOKMAKER_FILTER_MODES.CUSTOM, selectedIds: [] };
}

// =========================================================================
// validateTelegramBookmakerDraft
// =========================================================================

/**
 * Validates a local UI draft `{ mode, selectedIds }` and converts it
 * into the wire value the API accepts (`null | string[]`).
 *
 * - `draft` not a plain object → `{ ok: false, field: "bookmakers" }`.
 * - `draft.mode === "unlimited"` → `{ ok: true, value: null }`.
 * - `draft.mode === "custom"` with zero active/selectable ids →
 *   `{ ok: false, field: "bookmakers" }`.
 * - `draft.mode === "custom"` with at least one active id →
 *   `{ ok: true, value: [...normalizedIds] }` (a fresh array, in
 *   catalog order).
 * - Any other `mode` value → `{ ok: false, field: "bookmakers" }`.
 *
 * Never throws (fail-closed on any unexpected input shape, including
 * hostile Proxies) and never returns a reference to `draft` or any of
 * its nested values.
 *
 * @param {*} draft
 * @returns {{ok: true, value: null|string[]} | {ok: false, field: "bookmakers"}}
 */
function validateTelegramBookmakerDraft(draft) {
  try {
    if (!isPlainObject(draft)) {
      return { ok: false, field: "bookmakers" };
    }

    const mode = draft.mode;

    if (mode === TELEGRAM_BOOKMAKER_FILTER_MODES.UNLIMITED) {
      return { ok: true, value: null };
    }

    if (mode === TELEGRAM_BOOKMAKER_FILTER_MODES.CUSTOM) {
      const normalized = normalizeTelegramBookmakerIds(draft.selectedIds);
      if (normalized.length === 0) {
        return { ok: false, field: "bookmakers" };
      }
      return { ok: true, value: normalized.slice() };
    }

    return { ok: false, field: "bookmakers" };
  } catch (_e) {
    return { ok: false, field: "bookmakers" };
  }
}

// =========================================================================
// telegramBookmakerDraftEqualsSettings
// =========================================================================

/**
 * Compares a local UI draft against the server's `bookmakers` wire
 * value, entirely in terms of the normalized wire representation (not
 * raw input order/shape).
 *
 * - An invalid draft (including an empty custom draft) never equals
 *   anything → `false`.
 * - A valid "unlimited" draft equals server value `null` only.
 * - A valid "custom" draft equals a server array only if, once both
 *   sides are normalized through the shared catalog, they contain the
 *   exact same set of ids (order-independent on the input side; the
 *   comparison itself is order-stable because both sides are
 *   normalized to catalog order first).
 *
 * Never mutates `draft` or `bookmakers`. Never throws.
 *
 * @param {*} draft
 * @param {null|Array<*>|*} bookmakers
 * @returns {boolean}
 */
function telegramBookmakerDraftEqualsSettings(draft, bookmakers) {
  try {
    const validated = validateTelegramBookmakerDraft(draft);
    if (!validated.ok) {
      return false;
    }

    if (validated.value === null) {
      return bookmakers === null;
    }

    if (!Array.isArray(bookmakers)) {
      return false;
    }

    const normalizedServerIds = normalizeTelegramBookmakerIds(bookmakers);
    if (normalizedServerIds.length !== validated.value.length) {
      return false;
    }
    return validated.value.every((id, index) => id === normalizedServerIds[index]);
  } catch (_e) {
    return false;
  }
}

module.exports = {
  TELEGRAM_BOOKMAKER_FILTER_MODES,
  normalizeTelegramBookmakerIds,
  buildTelegramBookmakerDraftFromSettings,
  validateTelegramBookmakerDraft,
  telegramBookmakerDraftEqualsSettings,
};
